import React, { useState, useEffect, useMemo } from "react";
import { Globe, Pencil, Check, X, ExternalLink, Plus, Trash2, CheckCircle2, XCircle } from "lucide-react";
import http from "@/lib/http";
import { t } from "@/i18n";

/** Platform configuration. site_makelaar is only shown for makelaar leads. */
const ALL_PLATFORMS = ["zimmo", "immoweb", "immoscoop", "luxevastgoed", "tweedehands", "site_makelaar"];

export default function PlatformLinks({ lead, onChanged }) {
  const platforms = useMemo(() => {
    return ALL_PLATFORMS.filter(p => p !== "site_makelaar" || lead.source_type === "makelaar");
  }, [lead.source_type]);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const init = {};
    platforms.forEach(p => { init[p] = lead.platform_urls?.[p] || ""; });
    setDraft(init);
  }, [lead, platforms, editing]);

  const save = async () => {
    setErr(null);
    setSaving(true);
    try {
      const cleaned = {};
      platforms.forEach(p => { cleaned[p] = (draft[p] || "").trim(); });
      // Validate URLs (empty allowed)
      for (const [k, v] of Object.entries(cleaned)) {
        if (v && !/^https?:\/\//i.test(v)) {
          setErr(`${t(`leads.platforms.labels.${k}`)}: URL moet starten met http:// of https://`);
          setSaving(false);
          return;
        }
      }
      await http.patch(`/api/leads/${lead.id}/platform-urls`, { platform_urls: cleaned });
      setEditing(false);
      await onChanged?.();
    } catch (e) {
      setErr(e.response?.data?.detail || t("common.error_generic"));
    } finally { setSaving(false); }
  };

  return (
    <div className="card !p-4" data-testid="platform-links">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Globe size={14} className="text-[var(--teal-700)]" />
          <span className="text-[12px] uppercase tracking-wider text-[var(--ink-700)] font-semibold">
            {t("leads.platforms.title")}
          </span>
        </div>
        {!editing ? (
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setEditing(true)}
                    data-testid="platform-links-edit"
                    className="flex items-center gap-1 text-[12px] text-[var(--ink-500)] hover:text-[var(--teal-700)]">
              <Pencil size={12} /> {t("leads.platforms.edit")}
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setEditing(false)}
                    data-testid="platform-links-cancel"
                    className="text-[12px] text-[var(--ink-500)] hover:text-[var(--ink-900)]">
              {t("leads.platforms.cancel")}
            </button>
            <button type="button" onClick={save} disabled={saving}
                    data-testid="platform-links-save"
                    className="btn btn-primary !px-3 !py-1.5 h-8 text-[12px] disabled:opacity-50">
              <Check size={12} /> {saving ? t("common.loading") : t("leads.platforms.save")}
            </button>
          </div>
        )}
      </div>

      {!editing ? (
        <>
          {(() => {
            // Per-platform status — bron is uitsluitend Supabase (listing_urls):
            // een platform is online zolang zijn URL in listing_urls staat.
            // Manueel toegevoegde links overschrijven die status nooit.
            const liveSet = new Set(lead.listing_urls || []);
            const withUrl = platforms.filter(p => lead.platform_urls?.[p]);
            if (withUrl.length === 0) {
              return (
                <div className="text-[12px] text-[var(--ink-500)] italic py-2"
                     data-testid="platform-links-empty">
                  Geen URLs in Supabase — pand staat niet meer als zoekertje online.
                </div>
              );
            }
            const nOnline = withUrl.filter(p => liveSet.has(lead.platform_urls[p])).length;
            const nOffline = withUrl.length - nOnline;
            return (
              <>
                <div className="text-[11px] text-[var(--ink-500)] mb-3"
                     data-testid="platform-summary">
                  {nOnline} online{nOffline > 0 ? ` · ${nOffline} offline` : ""} — bron: Supabase
                </div>
                <div className="grid grid-cols-2 gap-2" data-testid="platform-links-grid">
                  {withUrl.map(p => {
                    const url = lead.platform_urls[p];
                    const isLive = liveSet.has(url);
                    return (
                      <a key={p} href={url} target="_blank" rel="noreferrer"
                         data-testid={`platform-${p}`}
                         data-live={isLive ? "active" : "offline"}
                         title={isLive
                           ? `Online — klik om te openen op ${t(`leads.platforms.labels.${p}`)}`
                           : "URL niet meer aanwezig in Supabase — pand staat op dit platform offline"}
                         className={`flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border text-[13px] transition cursor-pointer ${
                           isLive
                             ? "bg-[var(--teal-50)] border-[var(--teal-100)] text-[var(--teal-700)] hover:border-[var(--teal-400)]"
                             : "bg-[#FBF1F1] border-[#EFBABA] text-[var(--ink-500)] hover:border-[#D98C8C]"
                         }`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <Globe size={13} className="shrink-0" />
                          <span className="font-medium truncate">{t(`leads.platforms.labels.${p}`)}</span>
                        </div>
                        {isLive ? (
                          <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold shrink-0 text-[#1F8B5F]">
                            <CheckCircle2 size={11} /> Online
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold shrink-0 text-[#B43232]">
                            <XCircle size={11} /> Offline
                          </span>
                        )}
                      </a>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </>
      ) : (
        <div className="space-y-2.5" data-testid="platform-links-editor">
          {platforms.map(p => (
            <label key={p} className="block">
              <div className="flex items-center gap-2 mb-1">
                <Globe size={12} className="text-[var(--ink-500)]" />
                <span className="text-[11px] font-semibold text-[var(--ink-700)]">
                  {t(`leads.platforms.labels.${p}`)}
                </span>
              </div>
              <div className="flex gap-1.5">
                <input
                  type="url"
                  value={draft[p] || ""}
                  onChange={(e) => setDraft(d => ({ ...d, [p]: e.target.value }))}
                  placeholder={t("leads.platforms.placeholder")}
                  data-testid={`platform-input-${p}`}
                  className="flex-1 h-9 px-3 rounded-lg border border-[var(--border-soft)] bg-white font-mono text-[12px] outline-none focus:border-[var(--teal-500)]"
                />
                {draft[p] && (
                  <button type="button"
                          onClick={() => setDraft(d => ({ ...d, [p]: "" }))}
                          data-testid={`platform-clear-${p}`}
                          className="p-2 rounded-lg hover:bg-[var(--sand-100)] text-[var(--ink-500)] hover:text-[var(--danger)]">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </label>
          ))}
          {err && <div className="text-[12px] text-[var(--danger)]">{err}</div>}
          <div className="text-[11px] text-[var(--ink-500)] pt-1">
            {lead.source_type === "makelaar"
              ? t("leads.platforms.hint_makelaar")
              : t("leads.platforms.hint_particulier")}
          </div>
        </div>
      )}
    </div>
  );
}
